// Telephony for the confirmation page — the 3 call modes of a warehouse:
//   manual     → the agent clicks Call (click-to-call through the PBX + softphone)
//   auto       → browser-driven campaign: fetch next order → dial → wait for the outcome
//   predictive → the SERVER dials clients into a queue and bridges an answered one to
//                a free agent (this page joins/leaves the queue and pops the bridged order)
//
// Mixed into confirmation.vue. Everything here is prefixed `tp` to stay clear of the
// page's own state. The page provides (and this mixin relies on): `order`, `orderFound`,
// `showButtonStart`, `idWharhouse`, `currentUser`, `dateDebut`, `startConfirmation(silent)`,
// `loadOrderById(id)`, `getStatusByName(name)`, `CalculateTime()`.
//
// The PBX/softphone side is owned by the app-wide GlobalSoftphone (components/Telephony):
// we talk to it over the event bus (softphone:ensure-ready / :outbound / :expect) and read
// its shared reactive state (softphoneState).

import softphoneState from "../../common/softphoneState";
import bus from "../../common/eventBus";
import HTTP from "../../common/axios";

const webrtcPhone = require("../../common/webrtcPhone");

export default {
  data() {
    return {
      tpSoft: softphoneState, // reactive: useWebrtc / registered / inCall / connFailed
      tpEnabled: false, // telephony enabled for the current warehouse/order country
      tpMode: "manual", // manual | auto | predictive
      tpResolved: false, // callMode known (so the right Start block renders)
      tpCalling: false, // an originate is in flight
      // Set the instant a live call ends (inCall true→false) while an order is open →
      // drives the on-page "call ended — choose a status" banner. Cleared when the
      // agent saves a disposition (showButtonStart flips true) or starts a new call.
      tpCallEnded: false,
      // All SIMs busy → fair FIFO wait queue on the server
      tpBusy: { waiting: false, token: null, position: 0, total: 0, eta: 0, secs: 0 },
      // Predictive
      tpQueueJoined: false,
      tpQueueToggling: false,
      tpPredState: "", // "" | idle | connected
      // Auto-dialer
      tpAutoCfg: { delayBetweenCalls: 3, ringsBeforeNoAnswer: 5, recheckEmptyMins: 2, waitForClientAnswer: false },
      tpAuto: {
        running: false,
        paused: false,
        state: "", // waiting-line | dialing | ringing | answered | no-answer | empty | no-credit | paused
        text: "",
        stats: { called: 0, answered: 0, noAnswer: 0 },
        current: null,
        waitSecs: 0,
      },
    };
  },

  computed: {
    // A desktop (WebRTC) agent whose line isn't registered yet can't place a call.
    tpLineNotReady() {
      return !!(this.tpSoft.useWebrtc && !this.tpSoft.registered);
    },
    // This agent has a SIP line that is switched on (edit-user → extension + "enable").
    tpAgentLine() {
      const t = this.currentUser && this.currentUser.telephony;
      return !!(t && t.extension && t.active);
    },
    tpCanCall() {
      return !!(this.tpEnabled && this.tpAgentLine && this.order && this.order._id && !this.tpCalling && !this.tpBusy.waiting && !this.tpSoft.inCall && !this.tpLineNotReady);
    },
    // What drives the Start screen: the warehouse call mode AND an active line for THIS agent.
    //   auto-dialer → "Start campaign" · predictive → "Start receiving calls" · else → manual Start
    tpAutoOn() {
      return this.tpMode === "auto" && this.tpEnabled && this.tpAgentLine;
    },
    tpPredOn() {
      return this.tpMode === "predictive" && this.tpEnabled && this.tpAgentLine;
    },
    // Manual Start: manual mode, telephony off, or an agent without an active line. Only once
    // the mode is known, so the wrong button never flashes first.
    tpShowManualStart() {
      return this.tpResolved && !this.tpAutoOn && !this.tpPredOn;
    },
    // Auto/predictive is on for the warehouse but THIS agent has no active line → manual + hint.
    tpManualFallback() {
      return this.tpResolved && this.tpEnabled && this.tpMode !== "manual" && !this.tpAgentLine;
    },
    // The agent is ACTIVELY receiving (auto campaign running, or joined the predictive
    // queue) but no order is on screen yet → show the calm animated "waiting for the
    // next client" card instead of the big idle illustration. Hidden the moment an
    // order pops (showButtonStart flips false + order._id set) so the order UI shows.
    tpWaitingForClient() {
      const joined = (this.tpAutoOn && this.tpAuto && this.tpAuto.running) || (this.tpPredOn && this.tpQueueJoined);
      const noOrderYet = this.showButtonStart === true || !(this.order && this.order._id);
      return !!(joined && noOrderYet);
    },
    // The softphone's last hard error (mic blocked/busy/missing, or no audio). Written
    // by GlobalSoftphone but never shown on this page → a manual agent whose mic is
    // blocked saw the call silently die. Surface it as a dismissible banner. Returns a
    // translation key or "" (recent errors only — within 30s, so a stale one doesn't stick).
    tpLineError() {
      const e = this.tpSoft && this.tpSoft.lastError;
      if (!e || !e.kind) return "";
      if (e.at && (Date.now() - e.at) > 30000) return "";
      const map = {
        "mic-blocked": "tel_err_mic_blocked",
        "mic-busy": "tel_err_mic_busy",
        "mic-missing": "tel_err_mic_missing",
        "no-audio": "tel_err_no_audio",
      };
      return map[e.kind] || "tel_err_generic";
    },
  },

  watch: {
    // Warehouse known (or changed) → read its call mode so the right Start block shows.
    idWharhouse: {
      immediate: true,
      handler() {
        this.tpLoadMode();
      },
    },
    // An order loaded → is telephony enabled for ITS country?
    "order._id"(id) {
      if (id) this.tpLoadMode(true);
    },
    // The agent finished an order (every terminal handler flips showButtonStart back):
    //  • auto       → resume the campaign with the next order
    //  • predictive → free the slot so the next bridged client can pop
    showButtonStart(isStart) {
      if (!isStart) return;
      this.tpCallEnded = false;   // disposition saved → clear the "call ended" banner
      if (this.tpAuto.running && !this.tpAuto.paused && this.tpAuto.state === "answered") this.tpResumeAuto();
      if (this.tpQueueJoined) {
        this._tpPredBusy = false;
        this.tpPredState = "idle";
      }
    },
    // Live call ended (bridged → hung up) while an order is on screen → show the
    // on-page "call ended, choose a status" banner so the agent is guided to the next
    // action (the softphone's own "ended" cue lives in the detached floating widget
    // and is easy to miss). Flips true only on a real true→false transition of inCall.
    "tpSoft.inCall"(now, was) {
      if (was && !now && this.order && this.order._id && this.showButtonStart === false) {
        this.tpCallEnded = true;
        // PREDICTIVE safety: a dropped call must not keep the agent's slot claimed
        // forever. If the agent hasn't picked a disposition within 90s of the call
        // ending, auto-free the slot so the next client can still be bridged.
        if (this.tpQueueJoined) {
          clearTimeout(this._tpEndedSlotGuard);
          this._tpEndedSlotGuard = setTimeout(() => {
            if (this.tpCallEnded && this.tpQueueJoined) { this._tpPredBusy = false; this.tpPredState = "idle"; }
          }, 90 * 1000);
        }
      } else if (now) {
        this.tpCallEnded = false;   // a new call started → clear
        clearTimeout(this._tpEndedSlotGuard);
      }
    },
  },

  mounted() {
    // The page does NOT render an in-page inbound screen-pop — let the app-wide
    // GlobalSoftphone show its floating popup for customer callbacks.
    softphoneState.inboundPageActive = false;
    this._tpDestroyed = false;
  },

  beforeDestroy() {
    this._tpDestroyed = true;
    this.tpClearAutoTimers();
    this.tpCancelBusyTimers();
    this.tpStopPredWatch();
    this.tpDisarmUnloadGuard();
    softphoneState.suppressWidget = false;
    if (this.tpQueueJoined) this.tpBeaconLeaveQueue(); // leave the predictive queue on unmount
  },

  methods: {
    // ══════════════ status / call mode ══════════════
    // The warehouse whose telephony config applies: the loaded order's (country +
    // subWarehouse), else the header warehouse. The header warehouse is only used as
    // warehouseId when it's in the SAME country — otherwise a TD order would read the
    // MA header warehouse's call mode / SIMs. Same values go to status + joinQueue so
    // the server puts the agent in the queue of the warehouse this page shows.
    tpWarehouseCtx(fromOrder = false) {
      const cust = (fromOrder && this.order && this.order.customer) || {};
      const hdr = this.idWharhouse || {};
      const country = cust.country || hdr.country;
      const sw = cust.subWarehouse;
      const sameCountry = hdr.country && country && String(hdr.country).toUpperCase() === String(country).toUpperCase();
      const warehouseId = (sw && (sw._id || sw)) || (sameCountry ? hdr._id : null) || null;
      return { country, warehouseId };
    },

    async tpLoadMode(fromOrder = false) {
      const { country, warehouseId } = this.tpWarehouseCtx(fromOrder);
      if (!country) return;
      try {
        const res = await this.$server.customGet("telephony/status", { country, warehouseId });
        this.tpApplyMode((res && res.content) || {});
      } catch (e) {
        this.tpEnabled = false;
        this.tpResolved = true; // never leave the agent without a Start button
      }
    },

    tpApplyMode(c) {
      this.tpEnabled = !!c.enabled;
      this.tpMode = c.callMode === "auto" || c.callMode === "predictive" ? c.callMode : "manual";
      this.tpResolved = true;
      if (c.autoDialer) {
        this.tpAutoCfg = {
          delayBetweenCalls: Number(c.autoDialer.delayBetweenCalls) || 3,
          ringsBeforeNoAnswer: Number(c.autoDialer.ringsBeforeNoAnswer) || 5,
          recheckEmptyMins: Number(c.autoDialer.recheckEmptyMins) || 2,
          waitForClientAnswer: c.autoDialer.waitForClientAnswer === true,
        };
      }
      // Page refreshed while still a queue member server-side → restore the UI.
      if (this.tpPredOn && !this.tpQueueJoined && !this._tpQueueChecked) {
        this._tpQueueChecked = true;
        this.tpRestoreMembership();
      }
    },

    // Previous call attempts on THIS order (recordings + outcomes) → app-wide modal.
    tpOpenHistory() {
      if (this.order && this.order._id) bus.$emit("callHistory:open", { orderId: this.order._id });
    },

    // ══════════════ click-to-call ══════════════
    async tpCall() {
      const id = this.order && this.order._id;
      if (!id || this.tpCalling) return;
      if (this.tpSoft.inCall) {
        this.$toast.info(this.$t("tel_already_in_call"));
        return;
      }
      if (this.tpBusy.waiting) return; // already parked in the all-lines-busy queue
      if (this.tpLineNotReady) {
        this.$toast.warning(this.$t(this.tpSoft.connFailed ? "tel_webrtc_offline_hint" : "tel_webrtc_connecting"));
        return;
      }
      // Desktop agent: make sure the SIP registration is alive BEFORE dialing (the
      // call rings THIS browser first). Prime the mic on this page so the permission
      // prompt doesn't pop elsewhere when the softphone auto-answers.
      if (this.tpSoft.useWebrtc) {
        this.tpCalling = true;
        try { await webrtcPhone.warmUpMic(); } catch (e) { /* ignore */ }
        const ready = await new Promise((resolve) => {
          let done = false;
          const finish = (v) => { if (!done) { done = true; resolve(v); } };
          bus.$emit("softphone:ensure-ready", finish);
          setTimeout(() => finish(false), 6000);
        });
        this.tpCalling = false;
        if (!ready) {
          this.$toast.warning(this.$t("tel_webrtc_offline_hint"));
          return;
        }
      }
      this.tpCalling = true;
      if (this.tpSoft.useWebrtc && this.tpSoft.registered) {
        const number = (this.order.customer && (this.order.customer.shippingPhone || this.order.customer.phone)) || "";
        bus.$emit("softphone:outbound", { number });
      }
      try {
        // Arm the auto-answer window: the PBX rings the agent FIRST, and without this
        // the softphone would screen-pop it as a customer callback instead of answering.
        try { webrtcPhone.expectOutboundLeg(); } catch (e) { /* ignore */ }
        const res = await this.$server.customPost("telephony/call", { orderId: id });
        const c = res && res.content;
        if (c && c.ok) {
          this.$toast.success(this.$t("tel_call_in_progress"));
          this.tpCancelBusy();
        } else if (c && c.queued) {
          this.tpEnterQueue(c); // every SIM busy → fair FIFO queue
        } else if (c && c.allBusy) {
          this.$toast.warning(this.$t("tel_all_busy_giveup"));
        } else if (c && c.orderBusy) {
          this.$toast.warning(this.$t("tel_order_busy"));
        } else if (c && c.noGateway) {
          this.tpReleaseMic();
          this.tpCancelBusy();
          this.$toast.error(this.$t("tel_no_gateway"));
        } else if (c && c.extensionMissing) {
          this.tpReleaseMic();
          this.$toast.warning(this.$t("tel_ext_missing"));
        } else if (c && c.notConnected) {
          this.tpReleaseMic();
          this.$toast.warning(this.$t("tel_webrtc_not_ready"));
        }
      } catch (e) {
        /* surfaced by the shared axios layer */
      } finally {
        this.tpCalling = false;
      }
    },

    tpReleaseMic() {
      try { webrtcPhone.releasePendingMic && webrtcPhone.releasePendingMic(); } catch (e) { /* ignore */ }
    },

    tpHangup() {
      try { webrtcPhone.hangupWebrtc(); } catch (e) { /* ignore */ }
    },

    // ── all SIMs busy → fair FIFO queue: poll our spot, front-of-line claims the freed line ──
    tpEnterQueue(c) {
      this.tpBusy = { waiting: true, token: c.token, position: c.position || 1, total: c.total || 1, eta: c.eta || 5, secs: 0 };
      this.tpCancelBusyTimers();
      this._tpBusyPoll = setInterval(() => this.tpPollQueue(), 3000);
      this._tpBusyTick = setInterval(() => {
        this.tpBusy.eta = Math.max(0, this.tpBusy.eta - 1);
        this.tpBusy.secs += 1;
        if (this.tpBusy.secs > 180) { // a saturated queue is a capacity problem — stop waiting
          this.tpCancelBusy();
          this.$toast.warning(this.$t("tel_all_busy_giveup"));
        }
      }, 1000);
    },

    async tpPollQueue() {
      if (!this.tpBusy.token || this._tpBusyPolling) return;
      this._tpBusyPolling = true;
      const polled = this.tpBusy.token;
      try {
        const res = await this.$server.customPost("telephony/callQueueStatus", { token: polled });
        if (this.tpBusy.token !== polled) return; // cancelled meanwhile
        const c = res && res.content;
        if (!c) return;
        if (c.ok) {
          this.tpCancelBusy();
          this.$toast.success(this.$t("tel_call_in_progress"));
          if (this.tpSoft.useWebrtc && this.tpSoft.registered) {
            const number = (this.order.customer && (this.order.customer.shippingPhone || this.order.customer.phone)) || "";
            bus.$emit("softphone:outbound", { number });
          }
        } else if (c.waiting) {
          this.tpBusy.position = c.position || this.tpBusy.position;
          this.tpBusy.total = c.total || this.tpBusy.total;
          if (typeof c.eta === "number") this.tpBusy.eta = c.eta;
        } else if (c.expired) {
          this.tpCancelBusy();
          this.tpCall();
        } else if (c.extensionMissing || c.noGateway) {
          this.tpCancelBusy();
          this.$toast.error(this.$t(c.noGateway ? "tel_no_gateway" : "tel_ext_missing"));
        }
      } catch (e) {
        /* transient — the next tick retries */
      } finally {
        this._tpBusyPolling = false;
      }
    },

    tpCancelBusyTimers() {
      if (this._tpBusyTick) { clearInterval(this._tpBusyTick); this._tpBusyTick = null; }
      if (this._tpBusyPoll) { clearInterval(this._tpBusyPoll); this._tpBusyPoll = null; }
      this._tpBusyPolling = false;
    },

    // Stop waiting (call placed / cancelled / gave up) and release our FIFO spot.
    tpCancelBusy() {
      this.tpCancelBusyTimers();
      const token = this.tpBusy.token;
      if (token) this.$server.customPost("telephony/leaveCallQueue", { token }).catch(() => {});
      this.tpBusy = { waiting: false, token: null, position: 0, total: 0, eta: 0, secs: 0 };
    },

    // ══════════════ predictive queue ══════════════
    async tpToggleQueue() {
      if (this.tpQueueToggling) return;
      this.tpQueueToggling = true;
      const joining = !this.tpQueueJoined;
      try {
        // Join for the HEADER warehouse (the queue page isn't tied to one order).
        const res = await this.$server.customPost(
          joining ? "telephony/joinQueue" : "telephony/leaveQueue",
          joining ? this.tpWarehouseCtx(false) : {}
        );
        const c = (res && res.content) || {};
        if (c.ok) {
          this.tpQueueJoined = joining;
          this.$toast.success(this.$t(joining ? "tel_pd_joined" : "tel_pd_left"));
          if (joining) this.tpStartPredWatch();
          else this.tpStopPredWatch();
        } else if (c.extensionMissing) {
          this.$toast.warning(this.$t("tel_pd_no_ext"));
        } else if (c.notPredictive) {
          this.$toast.warning(this.$t("tel_pd_not_predictive"));
        } else {
          this.$toast.warning((c && c.error) || this.$t("tel_pd_queue_failed"));
        }
      } catch (e) {
        this.$toast.warning(this.$t("tel_pd_queue_failed"));
      } finally {
        this.tpQueueToggling = false;
      }
    },

    async tpRestoreMembership() {
      try {
        const res = await this.$server.customGet("telephony/queueMemberStatus", {});
        const c = (res && res.content) || {};
        if (c.joined && !this.tpQueueJoined) {
          this.tpQueueJoined = true;
          this.tpStartPredWatch();
        }
      } catch (e) { /* not joined / transient */ }
    },

    // The SERVER dials clients and bridges an answered one to a free agent; the browser
    // can't know the order ahead of time, so it polls queueBridgeState (which also
    // refreshes the membership heartbeat). On a bridge → load + show that order.
    tpStartPredWatch() {
      this.tpStopPredWatch();
      this.tpPredState = "idle";
      this._tpPredBusy = false;
      this.tpArmAutoAnswer();
      this.tpPredPoll();
    },
    // The server rings this softphone (the browser never triggers the call), so keep a
    // standing auto-answer window open — renewed on every poll.
    tpArmAutoAnswer() {
      try { bus.$emit("softphone:expect"); } catch (e) { /* ignore */ }
    },
    tpStopPredWatch() {
      if (this._tpPredTimer) { clearTimeout(this._tpPredTimer); this._tpPredTimer = null; }
      this.tpPredState = "";
      this._tpPredBusy = false;
      softphoneState.suppressWidget = false;
    },
    async tpPredPoll() {
      if (this._tpDestroyed || !this.tpQueueJoined) { this.tpStopPredWatch(); return; }
      if (this._tpPredBusy) { // an order is being handled — keep the heartbeat, don't pop another
        // queueBridgeState would consume a bridge / put us back in the queue, so the
        // heartbeat goes through queueMemberStatus instead (~15s; the server drops a
        // member after 60s of silence). Without it a call + disposition > 60s got the
        // agent purged from the queue mid-order.
        const now = Date.now();
        if (!this._tpBusyBeatAt || now - this._tpBusyBeatAt > 15000) {
          this._tpBusyBeatAt = now;
          this.$server.customGet("telephony/queueMemberStatus", {}).catch(() => {});
        }
        this._tpPredTimer = setTimeout(() => this.tpPredPoll(), 1500);
        return;
      }
      this.tpArmAutoAnswer();
      let orderId = null;
      try {
        const res = await this.$server.customGet("telephony/queueBridgeState", {});
        const c = (res && res.content) || {};
        orderId = c.orderId || null;
        if (c.extensionMissing && !this._tpExtWarned) {
          this._tpExtWarned = true;
          this.$toast.warning(this.$t("tel_pd_no_ext"));
        }
      } catch (e) { /* transient */ }
      if (this._tpDestroyed || !this.tpQueueJoined) { this.tpStopPredWatch(); return; }

      if (orderId) {
        this._tpPredBusy = true; // claim the slot BEFORE the async load (no double pop)
        this.tpPredState = "connected";
        let loaded = false;
        try { loaded = await this.loadOrderById(orderId, { bridged: true }); } catch (e) { loaded = false; }
        if (!loaded) { // assigned/consumed between the bridge stamp and the pop → free the slot
          this._tpPredBusy = false;
          this.tpPredState = "idle";
        } else {
          this.tpPlayReadyTone();
        }
      }
      this._tpPredTimer = setTimeout(() => this.tpPredPoll(), 1500);
    },

    // Leave the queue on unmount: fetch(keepalive) survives teardown and can carry the
    // auth header (axios can't fire during unload). If dropped anyway, the server
    // heartbeat TTL purges the member.
    tpBeaconLeaveQueue() {
      try {
        const token = window.localStorage.getItem("id_token");
        fetch(`${HTTP.defaults.baseURL}/telephony/leaveQueue`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Auth-Token": token || "" },
          body: "{}",
          keepalive: true,
        }).catch(() => {});
      } catch (e) {
        try { this.$server.customPost("telephony/leaveQueue", {}); } catch (x) { /* ignore */ }
      }
    },

    // Two short rising beeps the instant a real client is on the line and the order pops.
    tpPlayReadyTone() {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        if (!this._tpAudio || this._tpAudio.state === "closed") this._tpAudio = new Ctx();
        const ctx = this._tpAudio;
        const play = () => {
          const now = ctx.currentTime;
          [[880, 0], [1175, 0.12]].forEach(([freq, at]) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0, now + at);
            gain.gain.linearRampToValueAtTime(0.35, now + at + 0.01);
            gain.gain.linearRampToValueAtTime(0, now + at + 0.11);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(now + at);
            osc.stop(now + at + 0.1);
          });
        };
        if (ctx.state === "suspended") ctx.resume().then(play).catch(() => {});
        else play();
      } catch (e) { /* audio blocked → no cue, not fatal */ }
    },

    // ══════════════ auto-dialer (browser-driven campaign) ══════════════
    // Cycle: line free? → next order (same as Start) → dial (reuse tpCall) → outcome:
    //   answered   → the agent handles the order; when done the campaign resumes
    //   no answer  → mark unreached → next order
    tpStartAuto() {
      if (this.tpAuto.running || !this.tpAutoOn) return;
      this.tpClearAutoTimers();
      this._tpClientAnswered = false;
      this.tpAuto = { running: true, paused: false, state: "", text: "", stats: { called: 0, answered: 0, noAnswer: 0 }, current: null, waitSecs: 0 };
      softphoneState.suppressWidget = false;
      this.tpSetAutoState("waiting-line", this.$t("tel_ad_state_waiting_line"));
      // Unlock audio on this user gesture so the ready beep can play later.
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) {
          if (!this._tpAudio) this._tpAudio = new Ctx();
          if (this._tpAudio.state === "suspended") this._tpAudio.resume();
        }
      } catch (e) { /* ignore */ }
      this.tpArmUnloadGuard();
      this.tpAutoCycle();
    },

    tpPauseAuto() {
      if (!this.tpAuto.running) return;
      this.tpAuto.paused = !this.tpAuto.paused;
      if (this.tpAuto.paused) {
        this.tpSetAutoState("paused", this.$t("tel_ad_state_paused"));
        this.tpClearAutoTimers();
      } else {
        this.tpAutoCycle();
      }
    },

    // Stop: release an order that was dialed but not answered (it would otherwise stay
    // assigned to an agent who walked away), hang up, and return to the Start screen.
    tpStopAuto() {
      const st = this.tpAuto.state;
      if ((st === "dialing" || st === "ringing") && this.order && this.order._id) {
        this.tpMarkUnreachedQuiet(this.order, "no-answer").catch(() => {});
      }
      this.tpAuto.running = false;
      this.tpAuto.paused = false;
      this.tpAuto.stopping = true;
      softphoneState.suppressWidget = false;
      this.tpClearAutoTimers();
      this.tpSetAutoState("", "");
      this.tpDisarmUnloadGuard();
      if (this.tpSoft.inCall) this.tpHangup();
      this.tpCancelBusy();
      this._tpClientAnswered = false;
      this.tpAuto.current = null;
      this.tpResetOrder();
    },

    tpResetOrder() {
      this.order = { customer: {}, details: [], seller: {}, teleConsultant: {}, skippedBy: [], unreachedBy: [] };
      this.orderFound = false;
      this.showButtonStart = true;
      this.vShow = true;
    },

    tpClearAutoTimers() {
      ["_tpAutoTimer", "_tpAutoRecheck", "_tpAnsweredWatchdog"].forEach((k) => {
        if (this[k]) { clearTimeout(this[k]); this[k] = null; }
      });
      if (this._tpWaitTimer) { clearInterval(this._tpWaitTimer); this._tpWaitTimer = null; }
    },

    tpSetAutoState(state, text) {
      this.tpAuto.state = state;
      this.tpAuto.text = text || "";
    },

    tpStartWaitTimer() {
      this.tpAuto.waitSecs = 0;
      if (this._tpWaitTimer) clearInterval(this._tpWaitTimer);
      this._tpWaitTimer = setInterval(() => { if (!this.tpAuto.paused) this.tpAuto.waitSecs++; }, 1000);
    },

    // Bail out of any async step when the agent paused/stopped or left the page.
    tpAutoGuard() {
      return !this.tpAuto.running || this.tpAuto.paused || this.tpAuto.stopping || this._tpDestroyed;
    },

    // Is a SIM free right now? "free" | "busy" | "noCredit" | "offline" | "error".
    async tpCheckFreeLine() {
      // Same warehouse the call will dial through (order's), never a cross-country header one.
      const { country, warehouseId } = this.tpWarehouseCtx(true);
      try {
        const res = await this.$server.customGet("telephony/hasFreeLine", { country, warehouseId });
        const c = (res && res.content) || {};
        if (c.free) return "free";
        if (c.reason === "noCredit") this._tpNoCreditDevice = c.device || "";
        return c.reason || "offline";
      } catch (e) {
        return "error";
      }
    },

    async tpAutoCycle() {
      if (this.tpAutoGuard()) return;
      this.tpClearAutoTimers();
      this.tpAuto.stopping = false;
      this.tpSetAutoState("waiting-line", this.$t("tel_ad_state_waiting_line"));

      // 0) only pull an order when there is a SIM to dial it on
      const line = await this.tpCheckFreeLine();
      if (this.tpAutoGuard()) return;
      if (line !== "free") {
        if (line === "noCredit") {
          // Every remaining SIM is out of credit → stop and alert (don't loop on a dead SIM).
          this.tpAuto.paused = true;
          this.tpSetAutoState("no-credit", this.$t("tel_ad_no_credit_pause", { device: this._tpNoCreditDevice || "" }));
        } else if (line === "busy") {
          this._tpAutoTimer = setTimeout(() => { if (!this.tpAutoGuard()) this.tpAutoCycle(); }, 3000);
        } else {
          this.tpSetAutoState("empty", this.$t("tel_ad_no_gateway_wait"));
          this._tpAutoRecheck = setTimeout(() => { if (!this.tpAutoGuard()) this.tpAutoCycle(); }, (this.tpAutoCfg.recheckEmptyMins || 2) * 60 * 1000);
        }
        return;
      }

      // 1) next order — the SAME confirmation flow as the manual Start button
      this.tpSetAutoState("waiting-line", this.$t("tel_ad_state_fetching"));
      this.orderFound = false;
      try { await this.startConfirmation(true); } catch (e) { /* treated as empty below */ }
      if (this.tpAutoGuard()) return;

      // 2) nothing to call → re-check later
      if (!this.orderFound || !this.order || !this.order._id) {
        this.tpSetAutoState("empty", this.$t("tel_ad_state_empty"));
        this._tpAutoRecheck = setTimeout(() => { if (!this.tpAutoGuard()) this.tpAutoCycle(); }, (this.tpAutoCfg.recheckEmptyMins || 2) * 60 * 1000);
        return;
      }
      const cust = this.order.customer || {};
      this.tpAuto.current = { id: this.order._id, name: cust.fullName || "", tel: cust.phone || cust.shippingPhone || "", orderNo: this.order.id || "" };
      await this.tpAutoDial();
    },

    async tpAutoDial() {
      if (this.tpAutoGuard()) return;
      const orderId = this.order && this.order._id;
      if (!orderId) return this.tpAutoCycle();

      this.tpSetAutoState("dialing", this.$t("tel_ad_state_dialing"));
      this.tpStartWaitTimer();
      this.tpAuto.stats.called++;
      this._tpClientAnswered = false;
      this._tpBeeped = false;
      // Remember the previous lastCall so we can tell a NEW outcome from an old one.
      this._tpLastCallBaseline = (this.order.lastCall && this.order.lastCall.date) || null;

      // Clear any stale "customer answered" stamp from a previous call on this order.
      if (this.tpAutoCfg.waitForClientAnswer) {
        try { await this.$server.customGet("telephony/callState", { orderId, reset: 1 }); } catch (e) { /* ignore */ }
        if (this.tpAutoGuard()) return;
      }
      // Revive a dead softphone line first — dialing into the void leaves the loop polling nothing.
      if (this.tpSoft.useWebrtc) {
        const ready = await new Promise((resolve) => {
          let done = false;
          const finish = (v) => { if (!done) { done = true; resolve(v); } };
          bus.$emit("softphone:ensure-ready", finish);
          setTimeout(() => finish(false), 6000);
        });
        if (this.tpAutoGuard()) return;
        if (!ready) {
          this.tpSetAutoState("waiting-line", this.$t("tel_ad_state_line_reconnect"));
          this._tpAutoTimer = setTimeout(() => { if (!this.tpAutoGuard()) this.tpAutoCycle(); }, 5000);
          return;
        }
      }

      try { await this.tpCall(); } catch (e) { /* surfaced by tpCall */ }
      if (this.tpAutoGuard()) return;

      if (this.tpBusy.waiting) { // parked in the all-lines-busy queue → wait for the line, NOT unreached
        this.tpSetAutoState("waiting-line", this.$t("tel_ad_state_waiting_line"));
        this.tpWaitQueueThenPoll();
        return;
      }
      this.tpPollOutcome(0);
    },

    tpWaitQueueThenPoll() {
      if (this.tpAutoGuard()) return;
      if (this.tpBusy.waiting) {
        this._tpAutoTimer = setTimeout(() => this.tpWaitQueueThenPoll(), 1500);
        return;
      }
      this.tpSetAutoState("dialing", this.$t("tel_ad_state_dialing"));
      this.tpPollOutcome(0);
    },

    // The agent leg auto-answers immediately (the PBX needs it to Dial the customer), so
    // `inCall` does NOT mean the customer picked up. Otherwise the outcome arrives as a
    // fresh order.lastCall (end-of-call webhook) / a consumed pendingCall.
    async tpPollOutcome(attempt) {
      if (this.tpAutoGuard()) return;

      if (this.tpSoft.inCall) {
        if (this.tpAutoCfg.waitForClientAnswer && !this._tpClientAnswered) {
          // Hear the ring but DON'T pop the order until the CUSTOMER really answers.
          softphoneState.suppressWidget = true;
          this.tpSetAutoState("ringing", this.$t("tel_ad_state_ringing"));
          this.tpPollClientAnswer(0);
          return;
        }
        this.tpHandToAgent();
        return;
      }

      const ringSecs = (this.tpAutoCfg.ringsBeforeNoAnswer || 5) * 5;
      const maxAttempts = Math.ceil((ringSecs + 15) / 2);
      let fresh = null;
      try {
        const res = await this.$server.get("orders", { id: this.order._id });
        fresh = res && res.content;
      } catch (e) { /* keep polling */ }
      if (this.tpAutoGuard()) return;

      const lc = fresh && fresh.lastCall;
      const pcStatus = fresh && fresh.pendingCall && fresh.pendingCall.status;
      const dateChanged = lc && lc.date && lc.date !== this._tpLastCallBaseline;
      if (dateChanged || pcStatus === "consumed") {
        const st = lc && lc.callStatus;
        if (st === "answered") {
          this.tpAuto.stats.answered++;
          this.tpHandToAgent();
          return;
        }
        await this.tpAutoUnreached(st || "no-answer");
        return;
      }
      if (attempt >= maxAttempts) {
        await this.tpAutoUnreached("timeout");
        return;
      }
      this._tpAutoTimer = setTimeout(() => this.tpPollOutcome(attempt + 1), 2000);
    },

    // A real client is on the line: stop the loop, the agent owns the call now. The loop
    // resumes through the showButtonStart watcher once the agent finishes the order.
    tpHandToAgent() {
      this.tpPlayReadyTone();
      this.tpClearAutoTimers();
      this.tpSetAutoState("answered", this.$t("tel_ad_state_answered"));
      // Safety net: if the resume never fires, re-fetch after a generous delay.
      this._tpAnsweredWatchdog = setTimeout(() => {
        if (this.tpAutoGuard()) return;
        if (this.tpSoft.inCall) { this.tpHandToAgent(); return; }
        if (this.tpAuto.state !== "answered") return;
        this.tpAutoCycle();
      }, 8 * 60 * 1000);
    },

    // waitForClientAnswer: poll callState (~1s) until the customer really answers.
    async tpPollClientAnswer(attempt) {
      if (this.tpAutoGuard()) { softphoneState.suppressWidget = false; return; }
      if (!this.tpSoft.inCall) { // the call ended before anyone answered
        const dur = (this.order && this.order.lastCall && Number(this.order.lastCall.duration)) || 0;
        softphoneState.suppressWidget = false;
        await this.tpAutoUnreached(dur > 0 ? "voicemail" : "no-answer");
        return;
      }
      const maxAttempts = (this.tpAutoCfg.ringsBeforeNoAnswer || 5) * 5 + 30;
      let answered = false;
      let voicemail = false;
      try {
        const res = await this.$server.customGet("telephony/callState", { orderId: this.order && this.order._id });
        answered = !!(res && res.content && res.content.answered);
        voicemail = !!(res && res.content && res.content.voicemail);
      } catch (e) { /* keep polling */ }
      if (this.tpAutoGuard()) return;
      if (voicemail) { // answered in < 6s of ring = operator voicemail → unreached, never pop
        softphoneState.suppressWidget = false;
        await this.tpAutoUnreached("voicemail");
        return;
      }
      if (answered) {
        this._tpClientAnswered = true;
        softphoneState.suppressWidget = false;
        this.tpAuto.stats.answered++;
        this.tpHandToAgent();
        return;
      }
      if (attempt >= maxAttempts) {
        softphoneState.suppressWidget = false;
        await this.tpAutoUnreached("no-answer");
        return;
      }
      this._tpAutoTimer = setTimeout(() => this.tpPollClientAnswer(attempt + 1), 1000);
    },

    tpUnreachedComment(reason) {
      const map = {
        "no-answer": this.$t("tel_ad_reason_no_answer"),
        busy: this.$t("tel_ad_reason_busy"),
        failed: this.$t("tel_ad_reason_failed"),
        voicemail: this.$t("tel_ad_reason_voicemail"),
        timeout: this.$t("tel_ad_reason_no_answer"),
      };
      return map[reason] || this.$t("tel_ad_reason_no_answer");
    },

    // Unreached write — the SAME payload as the page's manual "Order Unreached" button
    // (full business pipeline on the server), minus the alerts/UI reset.
    async tpMarkUnreachedQuiet(order, reason) {
      const dataToEdit = JSON.parse(JSON.stringify(order));
      // lastCall/pendingCall are server-managed (telephony webhooks). This payload is a
      // deep copy of the in-memory order whose lastCall is stale → shipping it back would
      // wipe the real call + recording the webhook wrote (same bug as the manual Unreached
      // button; the backend also strips them, this keeps the payload clean). Read the
      // duration BEFORE deleting (used for timeConsumed below).
      const dur = (order.lastCall && Number(order.lastCall.duration)) || 0;
      delete dataToEdit.lastCall; delete dataToEdit.pendingCall;
      const id = await this.getStatusByName("Unreached");
      const nDate = new Date(new Date().toLocaleString("en-US", { timeZone: "Africa/Casablanca" }));
      dataToEdit.unreached = id;
      dataToEdit.teleConsultant = null;
      dataToEdit.lastUnreachedDate = nDate;
      if (dataToEdit.remind) { dataToEdit.remind = null; dataToEdit.isReminded = false; }
      dataToEdit.timeConsumed = dur;
      dataToEdit.time = dur;
      dataToEdit.dateDebut = this.dateDebut || nDate;
      dataToEdit.comment = this.tpUnreachedComment(reason);
      dataToEdit.dateUnrash = nDate;
      delete dataToEdit.updateStartDate;
      await this.$server.update("orders", dataToEdit);
    },

    async tpAutoUnreached(reason) {
      if (this.tpAutoGuard()) return;
      this.tpClearAutoTimers();
      this.tpSetAutoState("no-answer", this.$t("tel_ad_state_no_answer"));
      this.tpAuto.stats.noAnswer++;
      try {
        await this.tpMarkUnreachedQuiet(this.order, reason);
      } catch (e) {
        /* never stall the campaign on a failed write — move on */
      }
      if (this.tpAutoGuard()) return;
      this.order = { customer: {}, details: [], seller: {}, teleConsultant: {}, skippedBy: [], unreachedBy: [] };
      this.orderFound = false;
      this._tpAutoTimer = setTimeout(() => { if (!this.tpAutoGuard()) this.tpAutoCycle(); }, (this.tpAutoCfg.delayBetweenCalls || 3) * 1000);
    },

    // The agent finished the handed-over order → next one after the configured delay.
    tpResumeAuto() {
      if (!this.tpAuto.running || this.tpAuto.paused) return;
      this.tpClearAutoTimers();
      this.tpSetAutoState("waiting-line", this.$t("tel_ad_state_fetching"));
      this._tpAutoTimer = setTimeout(() => { if (!this.tpAutoGuard()) this.tpAutoCycle(); }, (this.tpAutoCfg.delayBetweenCalls || 3) * 1000);
    },

    // Anti-refresh guard while a campaign is running.
    tpArmUnloadGuard() {
      if (this._tpUnload) return;
      this._tpUnload = (e) => {
        if (this.tpAuto.running && !this.tpAuto.paused) {
          e.preventDefault();
          e.returnValue = "";
          return "";
        }
      };
      window.addEventListener("beforeunload", this._tpUnload);
    },
    tpDisarmUnloadGuard() {
      if (this._tpUnload) {
        window.removeEventListener("beforeunload", this._tpUnload);
        this._tpUnload = null;
      }
    },

    tpFormatSecs(s) {
      const m = Math.floor((s || 0) / 60);
      return `${m}:${String((s || 0) % 60).padStart(2, "0")}`;
    },
  },
};
